import PropTypes from 'prop-types'
import { Fragment, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { customFlagValue } from 'lib/utils/handle-flag/const'
import { checkApplicableVoucher } from 'components/pdp/carousel-images/voucher/voucher-checkout/utils'
import { getVoucherDesc } from 'components/pdp/carousel-images/voucher/utils'
import compose from 'recompose/compose'
import { withI18next } from 'lib/i18n/withI18next'
import Typo from 'constants/typography'
import CartProduct from '../cart-product'
import cartItemsDuck from './duck'
import CartDiamondDealBanner from '../cart-diamond/cart-diamond-deal-banner/CartDiamondDealBanner'
import CartSelectAllHeader from './cart-select-all-header'
import style from './cart-items.scss'

const CartItems = ({
  t,
  allowProductUpdate,
  cartProducts,
  fromSummary,
  refType,
  setError,
  isProductUpdated,
  showFallbackModal,
  dispatch,
}) => {
  // Every fresh cart payload carries the backend's own is_selected per line
  // (see backendSelectedKeys below), so it replaces the client-side
  // selectedItemKeys override. Otherwise that override, built from the
  // lines present when a checkbox was last touched, never contains an item
  // added afterwards: the new line renders unchecked while the backend (and
  // the payment summary) count it as selected.
  useEffect(() => {
    dispatch(cartItemsDuck.creators.restoreItemsSelected(undefined))
  }, [cartProducts, dispatch])
  const voucherCheckoutNewErrorFlagValue = customFlagValue({
    flagName: 'voucherCheckout',
  })
  const productItems = useMemo(() => {
    const items = cartProducts?.items?.map((product) => ({
      ...product,
      selectedAttributes: product.attributes.find(({ selected }) =>
        Boolean(selected),
      ),
    }))
    if (!items) return items
    // Free gifts always render last, regardless of where the backend
    // places them in the response (it's been seen first) -- everything
    // else keeps its relative order.
    const regularItems = items.filter((product) => product.type !== 'gift')
    const giftItems = items.filter((product) => product.type === 'gift')
    return [...regularItems, ...giftItems]
  }, [cartProducts])
  const hasDiamondProductInCart = (cartProducts?.items || []).some(
    (product) => product.type === 'diamond',
  )
  const isShowDiamondDealBanner = Boolean(cartProducts?.diamond_info?.deal)

  // The new cart design is the only one shipping now -- force it on rather
  // than gate it on the 'shoppingBagRedesign' Flagsmith flag, without
  // deleting the old-design branch (still in CartProduct and friends) in
  // case it's wanted again later.
  const isShoppingBagNewDesign = true

  const getDiamondVoucherNote = () => {
    const diamondVoucher = {
      ...cartProducts?.diamond_info?.voucher,
      is_applicable: 1,
    }
    const applicableDiamondVoucher = checkApplicableVoucher(
      diamondVoucher,
      cartProducts.items,
      cartProducts.summary.subtotal,
      voucherCheckoutNewErrorFlagValue,
    )
    const note = !applicableDiamondVoucher?.is_applicable
      ? getVoucherDesc(
          !applicableDiamondVoucher?.is_applicable,
          applicableDiamondVoucher,
          t,
        )
      : ''
    return note
  }

  const diamondVoucherNote = cartProducts?.diamond_info?.voucher
    ? getDiamondVoucherNote()
    : null

  if (!cartProducts?.items) {
    return null
  }

  // Matches the exclusion on the per-item checkbox (cart-product-checkbox):
  // free gifts and the Diamond membership item don't get one, so they
  // don't count here either.
  const selectableItems = productItems.filter(
    (product) => product.type !== 'gift' && product.type !== 'diamond',
  )
  const selectableItemKeys = selectableItems.map(
    (product) =>
      `${product.id_product}-${product.selectedAttributes?.id_product_attribute}`,
  )
  // The backend's own is_selected per item (selectable-cart's view-product
  // action, merged into cart.payload by Cart.getCart in lib/api/cart.js) --
  // CartSelectAllHeader's fallback default when nothing's been toggled
  // client-side this session yet, same reasoning as CartProduct's own
  // isItemSelected. Items can be unselected now that selectable-cart (not
  // current/view, which only ever returns is_selected ones) is the item
  // source, so this has to actually read is_selected instead of treating
  // every rendered line as selected.
  const backendSelectedKeys = selectableItems
    .filter((product) => product.is_selected !== false)
    .map(
      (product) =>
        `${product.id_product}-${product.selectedAttributes?.id_product_attribute}`,
    )
  // select-product on a sold-out attribute is rejected by the backend
  // (confirmed live: 400 "The specified product attribute is not
  // found") -- CartSelectAllHeader's own batch would otherwise include
  // it whenever "Select all" is used to re-select everything, failing
  // the whole batch (and every other item's selection change along with
  // it) over one line that was never selectable to begin with.
  const soldOutItemKeys = selectableItems
    .filter((product) => product.selectedAttributes?.stock?.is_sold_out)
    .map(
      (product) =>
        `${product.id_product}-${product.selectedAttributes?.id_product_attribute}`,
    )

  return (
    <Fragment>
      <style jsx>{style}</style>
      <div className="cart-items">
        {isShoppingBagNewDesign &&
          !fromSummary &&
          selectableItemKeys.length > 0 && (
            <CartSelectAllHeader
              allSelectableKeys={selectableItemKeys}
              defaultSelectedKeys={backendSelectedKeys}
              soldOutItemKeys={soldOutItemKeys}
            />
          )}
        {!hasDiamondProductInCart && isShowDiamondDealBanner && (
          <CartDiamondDealBanner dealInfo={cartProducts?.diamond_info?.deal} />
        )}
        {productItems.map((product, idx) => (
          <div
            key={`${product.id_product}-${product.selectedAttributes?.id_product_attribute}`}
          >
            <CartProduct
              allowProductUpdate={allowProductUpdate}
              fromSummary={fromSummary}
              product={product}
              refType={refType}
              selectedAttribute={product.selectedAttributes}
              setError={setError}
              isProductUpdated={isProductUpdated}
              showFallbackModal={showFallbackModal}
              isShoppingBagNewDesign={isShoppingBagNewDesign}
              defaultSelectedKeys={backendSelectedKeys}
            />
            {hasDiamondProductInCart &&
              idx === 0 &&
              isShowDiamondDealBanner && (
                <CartDiamondDealBanner
                  dealInfo={cartProducts?.diamond_info?.deal}
                />
              )}
          </div>
        ))}
        {diamondVoucherNote && (
          <div className="cart-items__discount-voucher-note">
            <div className={Typo.caption}> {diamondVoucherNote}</div>
          </div>
        )}
      </div>
    </Fragment>
  )
}

CartItems.defaultProps = {
  allowProductUpdate: undefined,
  cartProducts: undefined,
  fromSummary: false,
  refType: 'bag',
  setError: undefined,
  isProductUpdated: undefined,
  showFallbackModal: undefined,
}

CartItems.propTypes = {
  allowProductUpdate: PropTypes.bool,
  cartProducts: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({})),
    diamond_info: PropTypes.shape({
      deal: PropTypes.string,
      voucher: PropTypes.shape({}),
    }),
    summary: PropTypes.shape({
      subtotal: PropTypes.number,
    }),
  }),
  fromSummary: PropTypes.bool,
  setError: PropTypes.func,
  refType: PropTypes.string,
  isProductUpdated: PropTypes.func,
  showFallbackModal: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}

export default compose(
  connect((state) => ({
    isPhone: state.device.isPhone,
    cartProducts: state.cart.payload,
  })),
  withI18next(),
)(CartItems)
